import {useEffect, useRef, useState} from "react";

type State = {
  state: "idle" | "pending" | "pendingTouch" | "started";
  actions: Actions;
};

type Actions = {
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseUp?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseMove?: (e: React.MouseEvent<HTMLElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove?: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd?: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchCancel?: (e: React.TouchEvent<HTMLElement>) => void;
};

type Point = {x: number; y: number};

export type DragHandler = {
  onMove: (pos: Point) => void;
  onStop: (isCancelled: boolean) => void;
  onUnmount: () => void;
};

type DragHandlerCreator = (startPos: Point) => DragHandler | null;

const primaryButton = 0;
const sloppyClickThreshold = 5;
const isSloppyClickThresholdExceeded = (original: Point, current: Point) =>
  Math.abs(current.x - original.x) >= sloppyClickThreshold ||
  Math.abs(current.y - original.y) >= sloppyClickThreshold;

type DragCb = (opts: {
  action: "start" | "move" | "end" | "cancel";
  movement: [number, number];
}) => void;

export const setupDragHandlers = (createDragHandler: DragHandlerCreator) => {
  const unsubs: (() => void)[] = [];
  let cancelActiveDrag: (() => void) | null = null;
  const getIdleState = (): State => ({
    state: "idle",
    actions: {
      onMouseDown: (e) => {
        if (e.defaultPrevented) return;
        if (e.button !== primaryButton) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        state = getPendingState({startPos: {x: e.clientX, y: e.clientY}});
      },
      onTouchStart: (e) => {
        if (e.defaultPrevented) return;
        const t = e.touches[0];
        const startPos = {x: t.clientX, y: t.clientY};
        const timeoutId = setTimeout(() => {
          unsubs.splice(0, unsubs.length);
          state = getStartedState({startPos, isTouch: true});
        }, 100);
        unsubs.push(() => clearTimeout(timeoutId));
        state = getPendingTouchState({startPos});
      },
    },
  });

  const getPendingState = ({startPos}: {startPos: Point}): State => {
    const cancel = () => {
      state = getIdleState();
    };
    return {
      state: "pending",
      actions: {
        onMouseUp: cancel,
        onMouseDown: cancel,
        onMouseMove: (e) => {
          if (e.button !== primaryButton) return;
          const point = {x: e.clientX, y: e.clientY};
          if (isSloppyClickThresholdExceeded(startPos, point)) {
            e.preventDefault();
          }
          state = getStartedState({startPos, isTouch: false});
        },
      },
    };
  };

  const getPendingTouchState = ({startPos}: {startPos: Point}): State => {
    const cancel = () => {
      for (const fn of unsubs.splice(0, unsubs.length)) fn();
      state = getIdleState();
    };
    return {
      state: "pendingTouch",
      actions: {
        onTouchMove: (e: React.TouchEvent) => {
          const t = e.touches[0];
          const point = {x: t.clientX, y: t.clientY};
          if (isSloppyClickThresholdExceeded(startPos, point)) {
            cancel();
          } else {
            e.preventDefault();
          }
        },
        onTouchStart: cancel,
        onTouchEnd: cancel,
        onTouchCancel: cancel,
      },
    };
  };

  const getStartedState = (args: {startPos: Point; isTouch: boolean}): State => {
    const {isTouch, startPos} = args;
    const handler = createDragHandler(startPos);
    if (!handler) return getIdleState();
    unsubs.push(handler.onUnmount);
    const onMove = (pos: Point) => handler.onMove(pos);
    const endDrag = () => stopDrag(false);
    const cancelDrag = () => stopDrag(true);
    cancelActiveDrag = cancelDrag;
    const stopDrag = (isCancelled: boolean) => {
      state = getIdleState();
      cancelActiveDrag = null;
      handler.onStop(isCancelled);
      for (const fn of unsubs.splice(0, unsubs.length)) fn();
    };
    if (isTouch) {
      const onTouchMove = (e: TouchEvent) => {
        const t = e.touches[0];
        const point = {x: t.clientX, y: t.clientY};
        onMove(point);
      };
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("touchend", endDrag);
      window.addEventListener("touchcancel", cancelDrag);
      unsubs.push(() => {
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("touchend", endDrag);
        window.removeEventListener("touchcancel", cancelDrag);
      });
    } else {
      const onMouseMove = (e: MouseEvent) => {
        const point = {x: e.clientX, y: e.clientY};
        onMove(point);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", endDrag);
      unsubs.push(() => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", endDrag);
      });
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelDrag();
    };
    window.addEventListener("keydown", handleKeyDown);
    unsubs.push(() => {
      window.removeEventListener("keydown", handleKeyDown);
    });
    return {state: "started", actions: {}};
  };

  let state = getIdleState();

  return {
    handlers: Object.fromEntries(
      (
        [
          "onMouseDown",
          "onMouseUp",
          "onMouseMove",
          "onTouchStart",
          "onTouchMove",
          "onTouchEnd",
          "onTouchCancel",
        ] as const
      ).map((key) => [
        key,
        (e: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          state.actions[key]?.(e);
        },
      ])
    ),
    // Ends an active drag as cancelled (so onStop can restore styles) and removes all listeners.
    // Call it from an effect cleanup rather than running the unsubs directly.
    cancel: () => {
      if (cancelActiveDrag) {
        cancelActiveDrag();
      } else {
        state = getIdleState();
        for (const fn of unsubs.splice(0, unsubs.length)) fn();
      }
    },
  };
};

const setupMyDragHandler = (dragCb: DragCb) => {
  return setupDragHandlers((startPos) => {
    dragCb({action: "start", movement: [0, 0]});
    let lastPos = startPos;
    return {
      onMove: (pos) => {
        dragCb({action: "move", movement: [pos.x - startPos.x, pos.y - startPos.y]});
        lastPos = pos;
      },
      onStop: (isCancelled) => {
        dragCb({
          action: isCancelled ? "cancel" : "end",
          movement: [lastPos.x - startPos.x, lastPos.y - startPos.y],
        });
      },
      onUnmount: () => {},
    };
  });
};

export const useDrag = (onDragCb: DragCb) => {
  const dragCbRef = useRef(onDragCb);
  dragCbRef.current = onDragCb;

  const [{handlers, cancel}] = useState(() =>
    setupMyDragHandler((dragCb) => dragCbRef.current(dragCb))
  );
  useEffect(() => cancel, [cancel]);
  return handlers;
};
